import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Printer } from "lucide-react";
import api, { fmtJMD } from "../../lib/api";
import QRCode from "../../components/QRCode";

/**
 * Batch-print all 5x4 sticker labels for a delivery day.
 * Each label prints on its own page — designed for 5" x 4" label stock.
 */
export default function AdminManifestLabels() {
  const [params] = useSearchParams();
  const day = params.get("day") || new Date().toISOString().slice(0, 10);
  const [orders, setOrders] = useState(null);

  useEffect(() => {
    api.get(`/admin/manifest?day=${day}`).then((r) => {
      const all = [];
      for (const p of Object.keys(r.data.groups || {})) all.push(...r.data.groups[p]);
      setOrders(all);
    }).catch(() => setOrders([]));
  }, [day]);

  if (orders === null) return <div className="p-10 text-sm text-zinc-500">Loading…</div>;

  return (
    <div className="bg-zinc-50 min-h-screen" data-testid="admin-labels">
      <div className="max-w-5xl mx-auto px-5 sm:px-6 py-6 print:hidden">
        <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight mb-2">Print labels · {day}</h1>
        <p className="text-sm text-zinc-500 mb-4">
          {orders.length} label{orders.length !== 1 ? "s" : ""} — each prints on its own 5×4 page.
          Use your browser's print dialog to select label stock paper.
        </p>
        <button onClick={() => window.print()} className="btn-primary inline-flex items-center gap-2" data-testid="do-print-all">
          <Printer size={14} /> Print all
        </button>
      </div>

      <div className="labels-wrap max-w-5xl mx-auto px-5 sm:px-6 pb-10 space-y-4 print:p-0 print:space-y-0 print:max-w-none">
        {orders.map((o) => (
          <Label key={o.id} order={o} />
        ))}
      </div>

      <style>{`
        @media print {
          .print\\:hidden { display: none !important; }
          body { background: white; }
          .label-sheet {
            page-break-after: always;
            width: 5in; height: 4in;
            padding: 0.15in;
            margin: 0; box-sizing: border-box;
            box-shadow: none !important;
            border-radius: 0 !important;
            border: none !important;
          }
        }
        .label-sheet {
          width: 5in; max-width: 100%;
          min-height: 4in;
          padding: 0.2in;
          box-sizing: border-box;
        }
      `}</style>
    </div>
  );
}

function Label({ order }) {
  const totalPops = (order.items || []).reduce((s, it) => s + (it.quantity || 1), 0);
  return (
    <div className="label-sheet bg-white border border-zinc-300 rounded-lg mx-auto flex"
         data-testid={`label-${order.id}`}>
      <div className="flex-1 min-w-0 pr-3">
        <div className="text-[9px] uppercase tracking-widest text-zinc-500">Fruit Blossoms</div>
        <div className="font-display font-bold text-lg leading-tight mt-1 truncate">{order.customer_name}</div>
        <div className="text-xs text-zinc-700">{order.customer_phone}</div>
        <div className="text-xs text-zinc-700 mt-1 break-words">{order.address_line}</div>
        <div className="text-xs font-semibold mt-1">{order.parish} · {order.delivery_day}</div>
        <div className="mt-2 text-[10px] text-zinc-500 font-mono">#{order.id.slice(0, 8)}</div>
        <div className="mt-2 text-xs font-bold">
          {totalPops} pops · {fmtJMD(order.total)}
        </div>
        {(order.delivery_notes || order.notes) && (
          <div className="mt-2 text-[10px] text-zinc-600 italic break-words">
            Note: {order.delivery_notes || order.notes}
          </div>
        )}
      </div>
      <div className="flex-shrink-0 flex flex-col items-center justify-center">
        <QRCode value={order.id} size={96} />
        <div className="text-[8px] text-zinc-500 mt-1">Scan to update status</div>
      </div>
    </div>
  );
}
